import { useState } from "react";
import { Check, ChevronDown, Circle, X } from "lucide-react";
import FullWidthImageLightbox from "@/components/mobile/FullWidthImageLightbox";

function formatTimelineAt(iso) {
  const raw = String(iso || "").trim();
  if (!raw) return "";
  try {
    const d = new Date(raw);
    if (Number.isNaN(d.getTime())) return "";
    return d.toLocaleString("en-IN", {
      day: "numeric",
      month: "short",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
      timeZone: "Asia/Kolkata",
    });
  } catch {
    return "";
  }
}

function StepIcon({ state }) {
  if (state === "done") {
    return (
      <span className="cp-timeline-icon cp-timeline-icon--done" aria-hidden>
        <Check size={14} strokeWidth={3} />
      </span>
    );
  }
  if (state === "current") {
    return (
      <span className="cp-timeline-icon cp-timeline-icon--current" aria-hidden>
        <Circle size={10} className="cp-timeline-dot" />
      </span>
    );
  }
  if (state === "cancelled") {
    return (
      <span className="cp-timeline-icon cp-timeline-icon--cancelled" aria-hidden>
        <X size={14} strokeWidth={3} />
      </span>
    );
  }
  return <span className="cp-timeline-icon cp-timeline-icon--pending" aria-hidden />;
}

/**
 * Bake → Frost → Decor → Delivery timeline for the customer proof window.
 */
export default function CustomerProductionTimeline({ timeline = [], summary }) {
  const [openKey, setOpenKey] = useState(null);
  const [lightbox, setLightbox] = useState(null);

  if (!timeline?.length) return null;

  return (
    <section className="cp-timeline-card" aria-label="Cake progress">
      <div className="cp-timeline-head">
        <h2 className="cp-timeline-title">Your cake journey</h2>
        {summary ? (
          <p className="cp-timeline-sub">
            Step {summary.done_count} of {summary.total_count}
            {summary.current_label ? ` · ${summary.current_label}` : ""}
          </p>
        ) : null}
      </div>

      {summary ? (
        <div className="cp-timeline-progress" aria-hidden>
          <div
            className="cp-timeline-progress-fill"
            style={{ width: `${Math.max(summary.progress_pct || 0, summary.done_count ? 8 : 4)}%` }}
          />
        </div>
      ) : null}

      <ol className="cp-timeline-list">
        {timeline.map((step, i) => {
          const isLast = i === timeline.length - 1;
          const photos = step.proof_photos || [];
          const canOpen = photos.length > 0 && (step.state === "done" || step.state === "current");
          const isOpen = openKey === step.key;
          const dimmed = step.state === "pending";

          return (
            <li key={step.key || i} className={`cp-timeline-item${dimmed ? " cp-timeline-item--dim" : ""}`}>
              <div className="cp-timeline-rail">
                {!isLast ? (
                  <span
                    className={`cp-timeline-line${
                      step.state === "done" ? " cp-timeline-line--done" : ""
                    }`}
                    aria-hidden
                  />
                ) : null}
                <StepIcon state={step.state} />
              </div>

              <div className={`cp-timeline-body${isLast ? "" : " cp-timeline-body--spaced"}`}>
                {canOpen ? (
                  <button
                    type="button"
                    className="cp-timeline-toggle"
                    onClick={() => setOpenKey(isOpen ? null : step.key)}
                  >
                    <div className="cp-timeline-copy">
                      <p
                        className={`cp-timeline-label${
                          step.state === "current" ? " cp-timeline-label--current" : ""
                        }`}
                      >
                        {step.label}
                      </p>
                      {step.detail ? <p className="cp-timeline-detail">{step.detail}</p> : null}
                      {step.at ? (
                        <p className="cp-timeline-at">{formatTimelineAt(step.at)}</p>
                      ) : null}
                      {!isOpen ? <p className="cp-timeline-photos-hint">Tap to see photos</p> : null}
                    </div>
                    <ChevronDown
                      size={18}
                      className={`cp-timeline-chevron${isOpen ? " cp-timeline-chevron--open" : ""}`}
                      aria-hidden
                    />
                  </button>
                ) : (
                  <div className="cp-timeline-copy">
                    <p
                      className={`cp-timeline-label${
                        step.state === "current" ? " cp-timeline-label--current" : ""
                      }`}
                    >
                      {step.label}
                    </p>
                    {step.detail ? <p className="cp-timeline-detail">{step.detail}</p> : null}
                    {step.at ? <p className="cp-timeline-at">{formatTimelineAt(step.at)}</p> : null}
                  </div>
                )}

                {canOpen && isOpen ? (
                  <div
                    className={`cp-timeline-photos${
                      photos.length > 1 ? " cp-timeline-photos--grid" : ""
                    }`}
                  >
                    {photos.map((photo) => (
                      <button
                        key={photo.url}
                        type="button"
                        className="cp-timeline-photo-btn"
                        onClick={() => setLightbox(photo)}
                      >
                        <img src={photo.url} alt={photo.label || ""} loading="lazy" decoding="async" />
                        <span>{photo.label}</span>
                      </button>
                    ))}
                  </div>
                ) : null}
              </div>
            </li>
          );
        })}
      </ol>

      {lightbox ? (
        <FullWidthImageLightbox
          src={lightbox.url}
          label={lightbox.label}
          alt={lightbox.label}
          onClose={() => setLightbox(null)}
        />
      ) : null}
    </section>
  );
}
